import React, { useEffect, useState } from "react";
import { L as Link } from "../lib/i18n";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Trash2, Bell, BellRing, BellOff, Search, Mail, Loader2 } from "lucide-react";
import { isPushSupported, getCurrentSubscription, subscribePush, unsubscribePush, notificationPermission } from "../lib/push";

function describeFilters(f = {}, allListingsLabel = "All listings") {
  const out = [];
  if (f.brand) out.push(f.brand);
  if (f.fuel) out.push(f.fuel);
  if (f.transmission) out.push(f.transmission);
  if (f.min_price) out.push(`ab ${f.min_price} CHF`);
  if (f.max_price) out.push(`bis ${f.max_price} CHF`);
  if (f.max_km) out.push(`max ${f.max_km} km`);
  if (f.min_year) out.push(`ab ${f.min_year}`);
  if (f.q) out.push(`„${f.q}"`);
  if (f.features) out.push(`Ausstattung: ${(typeof f.features === "string" ? f.features.split(",") : f.features).length}`);
  return out.length ? out.join(" · ") : allListingsLabel;
}

function buildHref(f = {}) {
  const p = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => { if (v !== "" && v != null) p.set(k, v); });
  return `/listings?${p.toString()}`;
}

export default function SavedSearches() {
  const { t } = useLang();
  const ts = t.savedSearches;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // optimistic update tracker per search id
  const [updatingId, setUpdatingId] = useState(null);

  const reload = () => api.listSearches().then(setItems).finally(() => setLoading(false));
  useEffect(() => { reload(); }, []);

  // ── Web Push state ──
  const pushSupported = isPushSupported();
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [permission, setPermission] = useState(notificationPermission());

  useEffect(() => {
    if (!pushSupported) return;
    getCurrentSubscription().then((s) => setPushSubscribed(!!s));
  }, [pushSupported]);

  const enablePush = async () => {
    setPushBusy(true);
    try {
      await subscribePush();
      setPushSubscribed(true);
      setPermission("granted");
      toast.success("Push-Benachrichtigungen aktiviert");
    } catch (e) {
      toast.error(e?.message || "Push konnte nicht aktiviert werden");
    } finally {
      setPushBusy(false);
    }
  };
  const disablePush = async () => {
    setPushBusy(true);
    try {
      await unsubscribePush();
      setPushSubscribed(false);
      // Reset per-search toggles in UI (server retains push_enabled, but it won't fire)
      toast.success("Push-Benachrichtigungen deaktiviert");
    } catch {
      toast.error("Push konnte nicht deaktiviert werden");
    } finally {
      setPushBusy(false);
    }
  };
  const togglePerSearch = async (id, enabled) => {
    setUpdatingId(id);
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, push_enabled: enabled } : it)));
    try {
      await api.toggleSearchPush(id, enabled);
      toast.success(enabled ? "Push für diese Suche aktiv" : "Push für diese Suche aus");
    } catch {
      toast.error("Fehler beim Aktualisieren");
      reload();
    } finally {
      setUpdatingId(null);
    }
  };

  const onDelete = async (id) => {
    try { await api.deleteSearch(id); toast.success(ts.removed); reload(); }
    catch { toast.error(ts.removeError); }
  };

  const onChangeFrequency = async (id, freq) => {
    setUpdatingId(id);
    // optimistic update
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, email_frequency: freq } : it)));
    try {
      const updated = await api.patchSearch(id, { email_frequency: freq });
      // Server is source of truth — refresh just this item
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...updated } : it)));
      toast.success(ts.freqUpdated);
    } catch (e) {
      toast.error(ts.freqError);
      // Reload from server to get back to consistent state
      reload();
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8" data-testid="saved-searches-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">{t.nav.alerts}</p>
          <h1 className="mt-2 font-display text-3xl font-bold text-white sm:text-4xl">{ts.title}</h1>
          <p className="mt-2 text-sm text-zinc-400">{ts.subtitle}</p>
        </div>
        <Button asChild className="bg-red-600 hover:bg-red-500">
          <Link to="/listings"><Search className="mr-2 h-4 w-4" /> {ts.create}</Link>
        </Button>
      </div>

      <div className="mt-10">
        {/* Push opt-in card */}
        {pushSupported && (
          <div
            className="mb-4 flex flex-col gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-4 sm:flex-row sm:items-center sm:justify-between"
            data-testid="push-optin-card"
          >
            <div className="flex items-start gap-3">
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${pushSubscribed ? "bg-emerald-600/15 text-emerald-400" : "bg-red-600/15 text-red-400"}`}>
                {pushSubscribed ? <BellRing className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white">
                  {pushSubscribed ? "Push-Benachrichtigungen aktiv" : "Browser-Push aktivieren"}
                </p>
                <p className="mt-0.5 text-xs text-zinc-400">
                  {pushSubscribed
                    ? "Du erhältst eine Benachrichtigung sobald neue Treffer für eine deiner gespeicherten Suchen vorliegen."
                    : permission === "denied"
                      ? "Benachrichtigungen sind im Browser blockiert. Aktiviere sie in den Site-Einstellungen."
                      : "Sofort-Hinweis im Browser sobald neue Fahrzeuge zu deinen Suchen passen — keine E-Mail nötig."}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              {pushSubscribed ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={disablePush}
                  disabled={pushBusy}
                  className="border-zinc-800 bg-transparent text-zinc-300 hover:bg-zinc-900"
                  data-testid="push-disable-btn"
                >
                  {pushBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellOff className="mr-1.5 h-4 w-4" />}
                  Deaktivieren
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={enablePush}
                  disabled={pushBusy || permission === "denied"}
                  className="bg-red-600 text-white hover:bg-red-500 disabled:opacity-60"
                  data-testid="push-enable-btn"
                >
                  {pushBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <BellRing className="mr-1.5 h-4 w-4" />}
                  Push aktivieren
                </Button>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="h-32 animate-pulse rounded-lg border border-zinc-800 bg-zinc-950" />
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-800 p-16 text-center text-sm text-zinc-500">
            {ts.empty}<br />
            {ts.emptyHint}
          </div>
        ) : (
          <ul className="divide-y divide-zinc-800 rounded-lg border border-zinc-800 bg-zinc-950">
            {items.map((s) => {
              const freq = s.email_frequency || "daily";
              const isOff = freq === "off";
              return (
                <li key={s.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`search-${s.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Bell className={`h-4 w-4 ${!isOff && s.notify ? "text-red-500" : "text-zinc-600"}`} />
                      <p className="font-display text-lg font-semibold text-white truncate">{s.name}</p>
                    </div>
                    <p className="mt-1 text-xs text-zinc-400 truncate">{describeFilters(s.filters, ts.allListings)}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Per-search push toggle — only when global push is on */}
                    {pushSupported && pushSubscribed && (
                      <button
                        type="button"
                        onClick={() => togglePerSearch(s.id, !s.push_enabled)}
                        disabled={updatingId === s.id}
                        title={s.push_enabled ? "Push für diese Suche aus" : "Push für diese Suche an"}
                        className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs ${
                          s.push_enabled
                            ? "border-red-500/40 bg-red-600/10 text-red-300 hover:bg-red-600/20"
                            : "border-zinc-800 bg-black text-zinc-400 hover:text-white"
                        }`}
                        data-testid={`push-toggle-${s.id}`}
                      >
                        {s.push_enabled ? <BellRing className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
                        Push
                      </button>
                    )}
                    {/* Frequency selector */}
                    <label className="inline-flex items-center gap-2 rounded-md border border-zinc-800 bg-black px-2.5 py-1.5 text-xs text-zinc-300 sm:text-sm">
                      <Mail className="h-3.5 w-3.5 text-zinc-500" />
                      <span className="hidden text-[10px] uppercase tracking-[0.18em] text-zinc-500 sm:inline">{ts.freqLabel}</span>
                      <select
                        value={freq}
                        onChange={(e) => onChangeFrequency(s.id, e.target.value)}
                        disabled={updatingId === s.id}
                        className="bg-black text-zinc-100 outline-none disabled:opacity-50"
                        data-testid={`freq-select-${s.id}`}
                        aria-label={ts.freqLabel}
                      >
                        <option value="instant">{ts.freqInstant}</option>
                        <option value="daily">{ts.freqDaily}</option>
                        <option value="weekly">{ts.freqWeekly}</option>
                        <option value="off">{ts.freqOff}</option>
                      </select>
                    </label>
                    <Button asChild variant="outline" size="sm" className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900">
                      <Link to={buildHref(s.filters)}>{ts.apply}</Link>
                    </Button>
                    <Button onClick={() => onDelete(s.id)} variant="outline" size="sm" className="border-zinc-800 bg-transparent text-red-500 hover:bg-red-950 hover:text-red-400" data-testid={`delete-search-${s.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
